import { fetchMethods } from '@/types/FetchService.types';
import { createHttpHeader } from '@/utils/network/createHTTPHeaders';
import { decryptTreezorResponse } from '@/services/encryption/decryptTreezorResponse';
import { SymmetricTreezorEncryption } from '@/services/encryption/SymmetricTreezorEncryption';
import { store } from '@/store/configuration';
import {
  IEndUserKeys,
  IGlobalSupportUserKeys,
  IPairKey,
  ISupportUserKeys,
  userTypes,
} from '@/store/models/encryption.model';
import { getStaticDashboardConfig } from '@/hooks/useDashboardConfig';
import { fetchQuery } from '@/utils/queryClient';
import { getSupportId } from '@/features/dashboard-users/helpers/getSupportId';

export class KeyManagementService {
  static queryKey = 'Key Managment';
  static getSupportUserKey = async (passphrase: string): Promise<IPairKey | undefined> => {
    try {
      const config = getStaticDashboardConfig();

      const supportUserId = getSupportId();

      const supportUserKeysResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.GET, action: 'user', id: supportUserId, params: { type: userTypes.SUPPORTUSER } },
        ],
        `${config.api.baseUrl}/key-management/support-users/${supportUserId}/keys`,
        {
          headers: createHttpHeader(),
        },
      );

      if (supportUserKeysResponse.ok) {
        const supportUserEncryptedKey: ISupportUserKeys = await supportUserKeysResponse.json();

        const symmetricTreezorEncryption = SymmetricTreezorEncryption.getInstance();
        await symmetricTreezorEncryption.init();

        const privateKey = (await symmetricTreezorEncryption.decryptKey(
          supportUserEncryptedKey.encryptedPrivateKey.withUserSecret,
          passphrase,
        )) as string;

        return {
          publicKey: supportUserEncryptedKey.publicKey,
          privateKey,
        };
      }

      return Promise.reject(await supportUserKeysResponse.json());
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  static fetchGlobalSupportPublicKey = async (): Promise<IGlobalSupportUserKeys> => {
    try {
      const config = store.getState().dashboardConfig.config;
      if (!config) return Promise.reject('Dashboard Config not found');

      const globalSupportKeysResponse = await fetchQuery(
        [this.queryKey, { method: fetchMethods.GET, action: 'global', params: { type: userTypes.SUPPORTUSER } }],
        `${config.api.baseUrl}/key-management/keys/global-support`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<IGlobalSupportUserKeys>(globalSupportKeysResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  static setGlobalSupportPublicKey = async (
    globalSupportUserKeys: IGlobalSupportUserKeys,
  ): Promise<IGlobalSupportUserKeys> => {
    try {
      const config = store.getState().dashboardConfig.config;
      if (!config) return Promise.reject('Dashboard Config not found');

      const setGlobalSupportKeyResponse = await fetchQuery(
        [
          this.queryKey,
          {
            method: fetchMethods.PUT,
            action: 'global',
            id: globalSupportUserKeys.globalSupportPublicKey,
            params: { type: userTypes.SUPPORTUSER },
          },
        ],
        `${config.api.baseUrl}/key-management/keys/global-support`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(globalSupportUserKeys),
        },
      );

      return await decryptTreezorResponse<IGlobalSupportUserKeys>(setGlobalSupportKeyResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  static fetchSupportUserKeys = async (supportUserId: string): Promise<ISupportUserKeys> => {
    try {
      const config = store.getState().dashboardConfig.config;
      if (!config) return Promise.reject('Dashboard Config not found');

      const supportUserKeysResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.GET, action: 'user', id: supportUserId, params: { type: userTypes.SUPPORTUSER } },
        ],
        `${config.api.baseUrl}/key-management/support-users/${supportUserId}/keys`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<ISupportUserKeys>(supportUserKeysResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  static fetchEndUserKeys = async (endUserId: number): Promise<IEndUserKeys> => {
    try {
      const config = store.getState().dashboardConfig.config;
      if (!config) return Promise.reject('Dashboard Config not found');

      const fetchEndUserKeysResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.GET, action: 'user', id: endUserId, params: { type: userTypes.ENDUSER } },
        ],
        `${config.api.baseUrl}/key-management/users/${endUserId}/keys`,
        {
          headers: createHttpHeader(),
        },
      );

      return await decryptTreezorResponse<IEndUserKeys>(fetchEndUserKeysResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };

  static setSupportUserKeys = async (supportUserId: string, keys: ISupportUserKeys): Promise<ISupportUserKeys> => {
    try {
      const config = store.getState().dashboardConfig.config;
      if (!config) return Promise.reject('Dashboard Config not found');

      const setSupportUserKeysResponse = await fetchQuery(
        [
          this.queryKey,
          { method: fetchMethods.PUT, action: 'user', id: supportUserId, params: { type: userTypes.SUPPORTUSER } },
        ],
        `${config.api.baseUrl}/key-management/support-users/${supportUserId}/keys`,
        {
          headers: createHttpHeader(),
          body: JSON.stringify(keys),
        },
      );

      return await decryptTreezorResponse<ISupportUserKeys>(setSupportUserKeysResponse, {
        shouldSkipEncryption: true,
        isSingle: true,
      });
    } catch (error) {
      console.error(error);
      return Promise.reject(error);
    }
  };
}
